<template>
  <div class="viewer-area">
    <div class="viewer" ref="canvasContainer"></div>
    <div class="texture-control" ref="textureControl" style="display: none;">
    <!-- 缩小按钮 -->
      <div class="scale-down-handle" ref="scaleDownBtn" title="click to Shrink">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M5 12h14"/>
        </svg>
      </div>
        <!-- 放大按钮 -->
      <div class="scale-up-handle" ref="scaleUpBtn" title="click to Enlarge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 5v14M5 12h14"/>
        </svg>
      </div>
        <!-- 左旋转按钮 -->
      <div class="rotate-left-handle" ref="rotateLeftBtn" title="Rotate Left 15°">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
          <path d="M3 3v5h5"/>
        </svg>
      </div>
       <!-- 右旋转按钮 -->
      <div class="rotate-right-handle" ref="rotateRightBtn" title="Rotate Right 15°">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 12a9 9 0 1 1-9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
          <path d="M21 3v5h-5"/>
        </svg>
      </div>
       <!-- 向下移一层按钮 - 使用图片 -->
      <div class="layer-down-handle" ref="layerDownBtn" title="Move Layer Down">
        <img src="/images/layerdown1.png" width="20" height="20" style="display: block;">
      </div>
       <!-- 向上移一层按钮 - 使用图片 -->
      <div class="layer-up-handle" ref="layerUpBtn" title="Move Layer Up">
        <img src="/images/layerup1.png" width="20" height="20" style="display: block;">
      </div>
      <!-- 删除按钮 -->
      <div class="delete-handle" ref="deleteBtn" title="Delete">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"/>
        </svg>
      </div>
    </div>
  </div>
</template>

<script>

import { markRaw } from 'vue'
import { useDesignStore } from '@/stores/designStore'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { mapActions } from 'pinia'

export default {
  name: 'Viewer3D',

  props: {
    // viewOnly=true：禁止编辑（不注册 pointer 事件）、禁止缩放，只允许旋转查看
    viewOnly: {
      type: Boolean,
      default: false
    }
  },

  setup() {
    const store = useDesignStore()
    // 监听 store 的变化
    store.$subscribe((mutation, state) => {
      console.log('Store changed:', mutation.events)
    })
    return { store }
  },

  // 移除 computed 中的 mapState，改用 setup 返回的 store
  data() {
    return {
      isDraggingLogo: false,
      isMoving: false,
      currentEditingLogo: null,
      lastMouseX: 0,
      lastMouseY: 0,
      autoRotateEnabled: false,
      autoRotateTimer: null,
      autoRotateSpeed: 0.001,
      currentHighlightedPart: null,
      originalColors: new Map(),
      imagesReady: true, 
      // Three.js 相关变量
      
      // 常量
      partKeyMap: {
        'front': ['front', 'front panel', '前幅', 'body_front', 'frontpanel', 'front body', 'frontpart', 'bodyfront'],
        'back': ['back', 'back panel', '后幅', 'body_back', 'backpanel', 'back body', 'backpart', 'bodyback'],
        'collar': ['collar', 'neck', '领口', 'neckline'],
        'left sleeve': ['sleeve_left', 'left sleeve', 'lsleeve', 'left_sleeve', '左袖', 'sleeve_l', 'l_sleeve', 'leftsleeve', 'left arm', 'sleeveleft', 'larm'],
        'right sleeve': ['sleeve_right', 'right sleeve', 'rsleeve', 'right_sleeve', '右袖', 'sleeve_r', 'r_sleeve', 'rightsleeve', 'right arm', 'sleeveright', 'rarm'],
        'left side': ['side_left', 'left side', 'lside', 'left_panel', '侧左', 'leftside', 'left lateral', 'left flank', 'side_left', 'lateralleft'],
        'right side': ['side_right', 'right side', 'rside', 'right_panel', '侧右', 'rightside', 'right lateral', 'right flank', 'side_right', 'lateralright'],
        'pocket': ['pocket', 'pouch', '后口袋', 'back_pocket', 'pocketback'],
        'hood': ['hood', 'hat', 'cap', 'hoodie', 'hoody']
      },
      modelUrls: {
        'Short Sleeve': '/models/short_sleeve.glb',
        'Long Sleeve': '/models/longsleevecyclingjersey.glb',
        'Cycling Vest': '/models/vest001.glb',
        'Mx Jersey': '/models/mxjersey.glb',
        'Hoodie': '/models/hoodie008.glb',
        'T-shirt': '/models/t-shirt.glb',
        'Baseball Jersey': '/models/baseball_jersey.glb'
      },
      skipRotationForNextSelection: false,
      isLoading: false,
    }
  },

  watch: {
    'store.currentGarment': {
      handler(newVal, oldVal) {
        console.log('Viewer3D: store.currentGarment changed from', oldVal, 'to', newVal)
        if (newVal && newVal !== oldVal) {
          this.loadGarment(newVal)
        }
      },
      deep: true
    },
    'store.currentSelectedPart': {
      handler(newVal, oldVal) {
        console.log('Viewer3D: store.currentSelectedPart changed from', oldVal, 'to', newVal)
        console.log('Viewer3D: partsMap size =', this.partsMap ? this.partsMap.size : 0)
        
        // ★★★ 添加这个检查：如果正在加载或 partsMap 为空，跳过 ★★★
        if (this.isLoading) {
          console.log('Viewer3D: Skipping while loading')
          return
        }
        
        // ★★★ 关键修复：如果 partsMap 还没有准备好，等待模型加载完成 ★★★
        if (!this.partsMap || this.partsMap.size === 0) {
          console.log('Viewer3D: partsMap not ready, will retry in 200ms')
          // 延迟重试，等待模型加载
          setTimeout(() => {
            if (this.partsMap && this.partsMap.size > 0 && newVal === this.store.currentSelectedPart) {
              this.updatePartHighlight(newVal)
              if (!this.skipRotationForNextSelection) {
                this.rotateToPartView(newVal)
              } else {
                this.skipRotationForNextSelection = false
              }
            }
          }, 200)
          return
        }
        
        if (newVal && this.partsMap && this.partsMap.size > 0) {
          this.updatePartHighlight(newVal)
          if (!this.skipRotationForNextSelection) {
            console.log('Viewer3D: Rotating to part view for', newVal)
            this.rotateToPartView(newVal)
          } else {
            console.log('Viewer3D: Skipping rotation (already rotated)')
            this.skipRotationForNextSelection = false
          }
        }
      },
      immediate: true,
      deep: true
    },
    'store.userColors': {
      deep: true,
      handler() {
        if (this.partsMap && this.partsMap.size > 0) {
          this.applyColorsToModel()
        }
      }
    },
    
    // ========== 添加这个监听：监听选中图层的 change ==========
    'store.currentEditingLogo': {
        handler(newVal, oldVal) {
            if (newVal && newVal.part && newVal.id) {
                if (!this.currentEditingLogo ||
                    this.currentEditingLogo.id !== newVal.id ||
                    this.currentEditingLogo.part !== newVal.part) {
                    this.currentEditingLogo = { part: newVal.part, id: newVal.id, isText: !!newVal.isText };
                    this.$nextTick(() => {
                        this.showTextureControls();
                        this.compositePartTexture(newVal.part, newVal.id);
                    });
                }
            } else {
                if (this.currentEditingLogo !== null) {
                    this.currentEditingLogo = null;
                    this.$nextTick(() => {
                        this.hideTextureControls();
                    });
                }
            }
        },
        deep: true,
        immediate: true
    },
    // 监听 partLayersMap 变化，重新合成纹理
    'store.partLayersMap': {
      deep: true,
      handler(newVal) {
        // 仅在切换服装（currentEditingLogo 为 null）时重新合成所有部位纹理
        if (this.partsMap && this.partsMap.size > 0 && !this.store.currentEditingLogo) {
          for (const [part] of newVal.entries()) {
            if (this.partsMap.has(part)) {
              setTimeout(() => {
                this.compositePartTexture(part)
              }, 50)
            }
          }
        }
      }
    }
  },
    

  
  created() {
    // Three.js 对象直接挂到 this 上，不经过 Vue 响应式系统
    this.scene = null
    this.camera = null
    this.renderer = null
    this.controls = null
    this.raycaster = null
    this.mouse = null
    this.currentModel = null
    this.partsMap = new Map()
  },

  mounted() {
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.initScene()
        this.initLights()
        this.initTextureControls()
        this.initAutoRotate()
        this.loadGarment(this.store.currentGarment)
        window.addEventListener('resize', this.onWindowResize)
        window.$viewer3D = this
      })
    })
  },



  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize)
    if (this.autoRotateTimer) clearTimeout(this.autoRotateTimer)
    if (this.renderer) {
      this.renderer.dispose()
    }
  },
  methods: {
    ...mapActions(useDesignStore, [
      'updateLayerProperty',
      'deleteLayer',
      'clearLogoSelection',
      'selectLogo',
      'setPartColor',
      'reorderLayers',
      'setCurrentSelectedPart'
    ]),

    /**
     * 应用颜色到模型（不重新合成纹理）
     */
    applyColorsToModel() {
      this.store.userColors.forEach((color, part) => {
        if (this.partsMap.has(part)) {
          const material = this.partsMap.get(part)
          const hasLogos = this.store.partLayersMap.has(part) && this.store.partLayersMap.get(part).length > 0
          if (!hasLogos) {
            material.color.set(color)
            material.map = null
            material.needsUpdate = true
          } else {
            // 如果有 Logo，需要重新合成纹理以应用新底色
            this.compositePartTexture(part)
          }
        }
      })
    },

updateLayerOffset(deltaX, deltaY) {
  if (!this.currentEditingLogo) return;
  const layers = this.store.partLayersMap.get(this.currentEditingLogo.part);
  if (!layers) return;
  const targetLayer = layers.find(l => l.id === this.currentEditingLogo.id);
  if (targetLayer) {
    const newOffsetX = Math.max(-0.5, Math.min(0.5, (targetLayer.offsetX || 0) + deltaX));
    const newOffsetY = Math.max(-0.5, Math.min(0.5, (targetLayer.offsetY || 0) + deltaY));
    this.updateLayerProperty(this.currentEditingLogo.part, this.currentEditingLogo.id, 'offsetX', newOffsetX);
    this.updateLayerProperty(this.currentEditingLogo.part, this.currentEditingLogo.id, 'offsetY', newOffsetY);
    this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id);
  }
},

onTouchStart(event) {
  event.preventDefault();
  const touch = event.touches[0];
  // 需要通过射线检测是否点中贴图，复用 onPointerDown 的逻辑
  // 简化处理：如果有 currentEditingLogo 则允许拖拽
  if (this.currentEditingLogo) {
    this.isDraggingLogo = true;
    this.controls.enabled = false;
    this.lastMouseX = touch.clientX;
    this.lastMouseY = touch.clientY;
  }
},

onTouchMove(event) {
  if (!this.isDraggingLogo) return;
  event.preventDefault();
  const touch = event.touches[0];
  const deltaX = (touch.clientX - this.lastMouseX) * 0.002;
  const deltaY = (touch.clientY - this.lastMouseY) * 0.002;
  this.updateLayerOffset(deltaX, deltaY);
  this.lastMouseX = touch.clientX;
  this.lastMouseY = touch.clientY;
},

onTouchEnd() {
  this.isDraggingLogo = false;
  this.controls.enabled = true;
},


    async restoreDesignDataToModel() {
      console.log('Restoring design data to model...')
      console.log('userColors size:', this.store.userColors.size)
      console.log('partLayersMap size:', this.store.partLayersMap.size)
      
      // 等待一小段时间，确保模型已经完全处理
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // 1. 应用颜色
      if (this.partsMap && this.partsMap.size > 0) {
        this.applyColorsToModel()
      }
      
      // 收集所有需要等待的图片加载 Promise
      const loadPromises = []
      
      for (const [part, layers] of this.store.partLayersMap.entries()) {
        if (layers.length === 0 || !this.partsMap.has(part)) continue
        for (const layer of layers) {
          if (layer.url && (!layer.img || !layer.img.complete)) {
            if (!layer.img) {
              layer.img = new Image()
              layer.img.crossOrigin = 'anonymous'
              layer.img.src = layer.url
            }
            const promise = new Promise((resolve) => {
              if (layer.img.complete) {
                resolve()
              } else {
                layer.img.addEventListener('load', () => resolve(), { once: true })
                layer.img.addEventListener('error', () => resolve(), { once: true })
              }
            })
            loadPromises.push(promise)
          }
        }
      }
      
      // 等待所有图片加载完成
      if (loadPromises.length > 0) {
        await Promise.all(loadPromises)
      }
      
      // 所有图片就绪，合成纹理
      for (const [part, layers] of this.store.partLayersMap.entries()) {
        if (layers.length === 0 || !this.partsMap.has(part)) continue
        this.compositePartTexture(part)
      }
      
      this.imagesReady = true
      console.log('All images loaded, click detection enabled')
    },

    addTextToPartTexture(partName, textItem) {
      let layers = this.store.partLayersMap.get(partName) || []
      
      const newLayer = {
        id: textItem.id,
        url: null,
        img: textItem.img,
        scale: textItem.scale || 0.8,
        offsetX: textItem.offsetX || 0,
        offsetY: textItem.offsetY || 0,
        rotation: textItem.rotation || 0,
        isText: true,
        textContent: textItem.text,
        textFont: textItem.font,
        textColor: textItem.color,
        fontSize: textItem.fontSize
      }
      
      layers.push(newLayer)
      this.store.partLayersMap.set(partName, layers)
      this.compositePartTexture(partName)
    },

    initScene() {
      const container = this.$refs.canvasContainer
      if (!container) {
        console.error('canvasContainer ref not found!')
        return
      }
      
      this.scene = new THREE.Scene()
      this.scene.background = new THREE.Color(0xFFFFFF)
      
      const gridHelper = new THREE.GridHelper(3, 30, 0xd8d8d8, 0xdae2ea)
      gridHelper.position.y = -1
      this.scene.add(gridHelper)
      
      const aspect = container.clientHeight > 0 ? container.clientWidth / container.clientHeight : 1
      this.camera = markRaw(new THREE.PerspectiveCamera(45, aspect, 0.1, 1000))
      this.camera.position.set(0, 1.2, 3.5)
      
      this.renderer = markRaw(new THREE.WebGLRenderer({ antialias: true }))
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      const w = container.clientWidth || 600
      const h = container.clientHeight || 600
      this.renderer.setSize(w, h)
      this.renderer.outputColorSpace = THREE.SRGBColorSpace
      container.appendChild(this.renderer.domElement)
      
      this.controls = new OrbitControls(this.camera, this.renderer.domElement)
      this.controls.enableZoom = !this.viewOnly   // viewOnly 时禁止缩放
      this.controls.enableDamping = true
      this.controls.dampingFactor = 0.05
      this.controls.zoomSpeed = 1.2
      this.controls.rotateSpeed = 1.0
      this.controls.maxPolarAngle = Math.PI / 2.2
      this.controls.minPolarAngle = Math.PI / 6
      // ★★★ 禁止缩小，只允许放大 ★★★
      this.controls.minDistance = 0.5    // 最小距离（放大）
      this.controls.maxDistance = 3.0    // 最大距离（缩小限制）
      this.controls.addEventListener('change', () => {
        this.updateTextureControlPosition()
      })
      this.controls.addEventListener('start', this.stopAutoRotate)
      this.controls.addEventListener('end', this.resetIdleTimer)
      
      this.raycaster = new THREE.Raycaster()
      this.mouse = new THREE.Vector2()
      this.partsMap = new Map()
      this.currentModel = null
      
      this.setupRaycasterEvents()
      this.setupDragDropEvents()
      
      // 启动动画循环
      const animate = () => {
        requestAnimationFrame(animate)
        if (this.autoRotateEnabled && this.controls) {
          const target = this.controls.target
          const pos = this.camera.position
          const dx = pos.x - target.x
          const dz = pos.z - target.z
          const radius = Math.sqrt(dx * dx + dz * dz)
          const newAngle = Math.atan2(dx, dz) + this.autoRotateSpeed
          this.camera.position.set(target.x + Math.sin(newAngle) * radius, pos.y, target.z + Math.cos(newAngle) * radius)
          this.controls.target.copy(target)
        }
        if (this.controls) this.controls.update()
        if (this.renderer && this.scene && this.camera) {
          this.renderer.render(this.scene, this.camera)
        }
      }
      animate()
    },
    
    initLights() {
      const ambient = new THREE.AmbientLight(0xffffff, 0.5)
      this.scene.add(ambient)
      const mainLight = new THREE.DirectionalLight(0xffffff, 0.5)
      mainLight.position.set(0, 2, 2.5)
      this.scene.add(mainLight)
      const fillLight = new THREE.PointLight(0x6688aa, 0.5)
      fillLight.position.set(-1, 1, 1.5)
      this.scene.add(fillLight)
      const backLight = new THREE.DirectionalLight(0xffffff, 0.7)
      backLight.position.set(0, 1, -1.5)
      this.scene.add(backLight)
    },
    
    setupRaycasterEvents() {
      const canvas = this.renderer.domElement
      canvas.addEventListener('pointerdown', this.onPointerDown)
      canvas.addEventListener('pointermove', this.onPointerMove)
      canvas.addEventListener('pointerup', this.onPointerUp)
      canvas.addEventListener('pointerleave', this.onPointerUp)
      // viewOnly 模式下移除编辑相关的 pointer 事件
      if (this.viewOnly) {
        canvas.removeEventListener('pointerdown', this.onPointerDown)
        canvas.removeEventListener('pointermove', this.onPointerMove)
        canvas.removeEventListener('pointerup', this.onPointerUp)
        canvas.removeEventListener('pointerleave', this.onPointerUp)
      }
      
      // 添加触摸事件支持
      canvas.addEventListener('touchstart', this.onTouchStart)
      canvas.addEventListener('touchmove', this.onTouchMove)
      canvas.addEventListener('touchend', this.onTouchEnd)
    },
    
    setupDragDropEvents() {
      const canvas = this.renderer.domElement
      canvas.addEventListener('dragover', (e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
      })
      canvas.addEventListener('drop', this.onDrop)
    },
    
    getPartKeyFromName(name) {
      if (!name) return null
      const lowerName = name.toLowerCase().replace(/_/g, ' ').replace(/-/g, ' ')
      for (const [partKey, keywords] of Object.entries(this.partKeyMap)) {
        for (const kw of keywords) {
          const kwLower = kw.toLowerCase().replace(/_/g, ' ').replace(/-/g, ' ')
          if (lowerName.includes(kwLower) || kwLower.includes(lowerName)) {
            return partKey
          }
        }
      }
      return null
    },
    
    getModelUrl(garmentType) {
      return this.modelUrls[garmentType] || '/models/short_sleeve.glb'
    },
    
    centerAndFitModel(model) {
      const box = new THREE.Box3().setFromObject(model)
      const center = box.getCenter(new THREE.Vector3())
      const size = box.getSize(new THREE.Vector3())
      model.position.x -= center.x
      model.position.y -= center.y
      model.position.z -= center.z
      const minY = box.min.y - center.y
      model.position.y += Math.abs(minY) + 0.001
      
      const maxDim = Math.max(size.x, size.y, size.z)
      const fov = this.camera.fov * Math.PI / 180
      let distance = (maxDim / 2) / Math.tan(fov / 2)
      distance = Math.max(distance * 1.0, 2.2)
      
      const isMobile = window.innerWidth <= 768
      if (isMobile) {
        this.camera.position.set(0, 0.3, distance * 1.3)
        this.controls.target.set(0, size.y * 0.25, 0)
      } else {
        this.camera.position.set(distance * 0.4, distance * 0.5, distance * 1.2)
        this.controls.target.set(0, size.y * 0.4, 0)
      }
      this.controls.update()
    },
    
    loadGarment(type) {
      console.log('loadGarment called:', type)
      
      // 防止重复加载
      if (this.isLoading) {
        console.log('loadGarment: Already loading, skipping')
        return
      }
      
      if (!this.scene || !this.renderer) {
        console.warn('loadGarment called before Three.js initialized, will retry')
        const checkInterval = setInterval(() => {
          if (this.scene && this.renderer) {
            clearInterval(checkInterval)
            this.loadGarment(type)
          }
        }, 100)
        return
      }
      
      this.isLoading = true
      
      // 显示加载指示器
      const loadingOverlay = document.getElementById('loading-overlay')
      if (loadingOverlay) {
        loadingOverlay.style.display = 'flex'
      }
      
      const loader = new GLTFLoader()
      const modelPath = this.getModelUrl(type)
      
      // ========== 关键修复：彻底清理之前的模型资源 ==========
      if (this.currentModel) {
        // 1. 清理材质和纹理
        this.currentModel.traverse((child) => {
          if (child.isMesh) {
            // 清理几何体
            if (child.geometry) {
              child.geometry.dispose()
            }
            // 清理材质
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(mat => {
                  if (mat.map) {
                    mat.map.dispose()
                  }
                  mat.dispose()
                })
              } else {
                if (child.material.map) {
                  child.material.map.dispose()
                }
                child.material.dispose()
              }
            }
          }
        })
        // 2. 从场景中移除模型
        this.scene.remove(this.currentModel)
        // 3. 清空引用
        this.currentModel = null
      }
      
      // 清空 partsMap 和原始颜色记录
      this.partsMap.clear()
      this.originalColors.clear()
      this.currentHighlightedPart = null
      
      // 确保场景中没有残留的模型
      const modelsToRemove = []
      this.scene.children.forEach(child => {
        if (child.isGroup || child.isObject3D) {
          if (child.userData && child.userData.isModel) {
            modelsToRemove.push(child)
          }
        }
      })
      modelsToRemove.forEach(model => {
        this.scene.remove(model)
      })
      
      loader.load(modelPath, async (gltf) => {
        this.currentModel = gltf.scene
        this.currentModel.userData.isModel = true
        this.scene.add(this.currentModel)
        this.centerAndFitModel(this.currentModel)
        
        const isMobile = window.innerWidth <= 768
        if (isMobile) {
          this.currentModel.position.y -= 0.6
        } else {
          this.currentModel.position.y -= 0.25
        }
        
        // 收集部位材质映射
        this.currentModel.traverse((child) => {
          if (child.isMesh && child.material) {
            child.material.side = THREE.DoubleSide
            child.material.roughness = 0.6
            child.material.metalness = 0.01
            child.material.emissive = new THREE.Color(0x000000)
            child.material.emissiveIntensity = 0
            child.material.needsUpdate = true
            
            const partKey = this.getPartKeyFromName(child.name)
            if (partKey) {
              this.partsMap.set(partKey, child.material)
              this.originalColors.set(partKey, child.material.color.getHex())
            }
          }
        })
        
        console.log('Model loaded, partsMap keys:', Array.from(this.partsMap.keys()))
        
        // 应用 Store 中的颜色和纹理到模型
        await this.restoreDesignDataToModel()
        this.$emit('model-loaded', { partsMap: this.partsMap })
        // ========== 强制切换到 front 部位（使用标志位防止循环）==========
        // 仅高亮当前选中的部位，不强制切换 store 中的 currentSelectedPart
        if (this.partsMap && this.partsMap.size > 0) {
          const currentPart = this.store.currentSelectedPart || 'front'
          this.updatePartHighlight(currentPart)
          // 不自动旋转，避免干扰用户
        }
        
        // 隐藏加载指示器
        if (loadingOverlay) {
          setTimeout(() => {
            loadingOverlay.style.display = 'none'
          }, 200)
        }
        
        this.$emit('model-loaded', { partsMap: this.partsMap })
        window.dispatchEvent(new CustomEvent('modelSwitched', { 
          detail: { garmentType: type } 
        }))
        console.log('Model loaded successfully')
        
        // 加载完成，重置标志
        this.isLoading = false
        
      }, undefined, (err) => {
        console.error('Model load error:', err)
        if (loadingOverlay) {
          loadingOverlay.style.display = 'none'
        }
        this.isLoading = false
      })
    },
    
    darkenColor(hex, factor) {
      const r = Math.max(0, Math.min(255, Math.floor(((hex >> 16) & 0xFF) * factor)))
      const g = Math.max(0, Math.min(255, Math.floor(((hex >> 8) & 0xFF) * factor)))
      const b = Math.max(0, Math.min(255, Math.floor((hex & 0xFF) * factor)))
      return (r << 16) | (g << 8) | b
    },
    
    compositePartTexture(partName, highlightLogoId = null, highlightPart = false) {
      const material = this.partsMap.get(partName)
      if (!material) return
      
      let baseColor = this.store.userColors.has(partName) 
        ? this.store.userColors.get(partName) 
        : (this.originalColors.get(partName) ?? 0xffffff)
      const fillColor = '#' + baseColor.toString(16).padStart(6, '0')
      
      const layers = this.store.partLayersMap.get(partName) || []
      if (layers.length === 0) {
        material.map = null
        if (highlightPart) {
          const darkenedColorNum = this.darkenColor(baseColor, 0.9)
          material.color.set(darkenedColorNum)
        } else {
          material.color.set(baseColor)
        }
        material.needsUpdate = true
        return
      }
      
      const canvas = document.createElement('canvas')
      const size = 1024
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')
      
      ctx.fillStyle = fillColor
      ctx.fillRect(0, 0, size, size)
      
      if (highlightPart && !highlightLogoId) {
        ctx.fillStyle = 'rgba(0,0,0,0.05)'
        ctx.fillRect(0, 0, size, size)
      }
      
      layers.forEach(layer => {
        if (!layer.img) return
        if (!layer.img.complete) {
          layer.img.addEventListener('load', () => {
            this.compositePartTexture(partName, highlightLogoId, highlightPart)
          })
          return
        }
        const img = layer.img
        const imgAspect = img.width / img.height
        let drawW, drawH
        
        if (imgAspect >= 1) {
          drawW = (layer.scale || 1) * (img.width / 2)
          drawH = drawW / imgAspect
        } else {
          drawH = (layer.scale || 1) * (img.height / 2)
          drawW = drawH * imgAspect
        }
        
        const x = (size - drawW) / 2 + (layer.offsetX || 0) * size
        const y = (size - drawH) / 2 + (layer.offsetY || 0) * size
        const rotation = layer.rotation || 0
        
        if (rotation !== 0) {
          const cx = x + drawW / 2
          const cy = y + drawH / 2
          ctx.save()
          ctx.translate(cx, cy)
          ctx.rotate(rotation * Math.PI / 180)
          ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH)
          ctx.restore()
        } else {
          ctx.drawImage(img, x, y, drawW, drawH)
        }
        
        if (highlightLogoId && layer.id === highlightLogoId) {
            ctx.globalCompositeOperation = 'source-atop';
            ctx.fillStyle = 'rgba(0,0,0,0.15)';

            let highlightX = x, highlightY = y, highlightW = drawW, highlightH = drawH;

            // ★ 对文字图层进行像素级裁剪，使高亮区域贴合文字实际笔画
            if (layer.isText && layer.img) {
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = img.width;
                tempCanvas.height = img.height;
                const tempCtx = tempCanvas.getContext('2d');
                tempCtx.drawImage(img, 0, 0);
                const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
                const data = imageData.data;

                // 计算文字实际像素范围
                let minX = tempCanvas.width, minY = tempCanvas.height, maxX = 0, maxY = 0;
                let hasPixel = false;
                for (let y = 0; y < tempCanvas.height; y++) {
                    for (let x = 0; x < tempCanvas.width; x++) {
                        const alpha = data[(y * tempCanvas.width + x) * 4 + 3];
                        if (alpha > 0) {
                            hasPixel = true;
                            minX = Math.min(minX, x);
                            minY = Math.min(minY, y);
                            maxX = Math.max(maxX, x);
                            maxY = Math.max(maxY, y);
                        }
                    }
                }
                if (hasPixel) {
                    const textActualW = maxX - minX + 1;
                    const textActualH = maxY - minY + 1;
                    const scaleX = drawW / tempCanvas.width;
                    const scaleY = drawH / tempCanvas.height;

                    const actualDrawW = textActualW * scaleX;
                    const actualDrawH = textActualH * scaleY;

                    // 高亮背景大小：宽度扩大20%，高度扩大10%（或更小，如1.05）
                    const bgW = actualDrawW * 1.2;
                    const bgH = actualDrawH * 1.1;   // ← 关键：高度只扩展10%，避免过高

                    const bgX = x + (minX * scaleX) + (actualDrawW - bgW) / 2;
                    const bgY = y + (minY * scaleY) + (actualDrawH - bgH) / 2;

                    highlightX = bgX;
                    highlightY = bgY;
                    highlightW = bgW;
                    highlightH = bgH;
                }
            }

            // 处理旋转（如果存在）
            const rotationAngle = layer.rotation || 0;
            if (rotationAngle !== 0) {
                const cx = highlightX + highlightW / 2;
                const cy = highlightY + highlightH / 2;
                ctx.save();
                ctx.translate(cx, cy);
                ctx.rotate(rotationAngle * Math.PI / 180);
                ctx.fillRect(-highlightW / 2, -highlightH / 2, highlightW, highlightH);
                ctx.restore();
            } else {
                ctx.fillRect(highlightX, highlightY, highlightW, highlightH);
            }
            ctx.globalCompositeOperation = 'source-over';
        }
      })
      
      if (material.map) material.map.dispose()
      const texture = new THREE.CanvasTexture(canvas)
      texture.needsUpdate = true
      material.map = texture
      // 修复：使用纹理时，材质颜色应设为白色以正确显示纹理，但不要改变整体亮度
      // 保持原来的颜色值不变，纹理透明度使用 1.0
      material.color.set(0xffffff)
      // 确保纹理的透明度混合正确
      material.transparent = true
      material.opacity = 1.0
      material.needsUpdate = true
    },
    
    applyColors() {
      this.applyColorsToModel()
    },
    
   applyAllTextures() {
      for (const [part] of this.store.partLayersMap.entries()) {
        if (this.partsMap.has(part)) {
          this.compositePartTexture(part)
        }
      }
    },
        
    /**
     * 专门处理部位高亮的逻辑
     */
    updatePartHighlight(part) {
      console.log('updatePartHighlight called:', part)
      
      if (part !== 'whole' && !this.partsMap.has(part)) {
        console.log('Part not found in partsMap:', part)
        return
      }

      this.currentHighlightedPart = part

      const getBaseColor = (partName) => {
        if (this.store.userColors.has(partName)) return this.store.userColors.get(partName)
        return this.originalColors.get(partName) || 0xffffff
      }

      // 1. 将所有部位恢复为基准色（不加深）
      this.partsMap.forEach((material, name) => {
        const hasLogos = this.store.partLayersMap.has(name) && this.store.partLayersMap.get(name).length > 0
        if (hasLogos) {
          this.compositePartTexture(name, null, false)
        } else {
          material.map = null
          material.color.set(getBaseColor(name))
          material.needsUpdate = true
        }
      })

      // 2. 对当前选中部位进行加深
      if (part !== 'whole') {
        const material = this.partsMap.get(part)
        if (material) {
          const hasLogos = this.store.partLayersMap.has(part) && this.store.partLayersMap.get(part).length > 0
          if (hasLogos) {
            this.compositePartTexture(part, null, true)
          } else {
            const baseColorNum = getBaseColor(part)
            const darkenedColorNum = this.darkenColor(baseColorNum, 0.95)
            material.color.set(darkenedColorNum)
            material.needsUpdate = true
          }
        }
      } else {
        // part === 'whole': 将所有部位加深
        this.partsMap.forEach((material, name) => {
          const hasLogos = this.store.partLayersMap.has(name) && this.store.partLayersMap.get(name).length > 0
          if (hasLogos) {
            this.compositePartTexture(name, null, true)
          } else {
            const baseColorNum = getBaseColor(name)
            const darkenedColorNum = this.darkenColor(baseColorNum, 0.92)
            material.color.set(darkenedColorNum)
            material.needsUpdate = true
          }
        })
      }
    },
    
    // 选择部位（选中 + 旋转）- 供 PartSelector 调用
  selectPart(part) {
    console.log('Viewer3D: selectPart called (with rotation):', part)
    if (!part) return
    
    console.log('Viewer3D: partsMap size =', this.partsMap ? this.partsMap.size : 0)
    console.log('Viewer3D: currentModel =', this.currentModel ? 'exists' : 'null')
    
    if (!this.partsMap || this.partsMap.size === 0) {
      console.warn('partsMap not ready, retrying...')
      setTimeout(() => this.selectPart(part), 200)
      return
    }
    
    if (!this.currentModel) {
      console.warn('currentModel not ready, retrying...')
      setTimeout(() => this.selectPart(part), 200)
      return
    }
    
    // 设置标志位，避免 watch 重复旋转
    this.skipRotationForNextSelection = true
    
    // 更新 store
    this.setCurrentSelectedPart(part)
    
    // 执行高亮和旋转
    console.log('Viewer3D: Executing updatePartHighlight for', part)
    this.updatePartHighlight(part)
    
    console.log('Viewer3D: About to call rotateToPartView for', part)
    this.rotateToPartView(part)
    
    // 延迟重置标志位
    setTimeout(() => {
      this.skipRotationForNextSelection = false
    }, 300)
  },

    // 只选中部位，不旋转 - 供 3D 模型点击调用
    selectPartOnly(part) {
      console.log('Viewer3D: selectPartOnly called (no rotation):', part)
      if (!part) return
      this.skipRotationForNextSelection = true
      // 更新 store
      this.setCurrentSelectedPart(part)
        
      setTimeout(() => {
        this.skipRotationForNextSelection = false
      }, 100)
      // 只执行高亮，不旋转
      if (this.partsMap && this.partsMap.size > 0) {
        this.updatePartHighlight(part)
      }
    },
    
    getPartAngle(partName) {
      const baseAngles = {
        'front': 0,
        'back': Math.PI,
        'left sleeve': Math.PI / 2,
        'right sleeve': -Math.PI / 2,
        'left side': Math.PI / 2,
        'right side': -Math.PI / 2,
        'collar': 0,
        'hood': 0,
        'whole': 0
      }
      if (partName === 'pocket') {
        if (this.store.currentGarment === 'Hoodie') {
          return 0
        } else {
          return Math.PI
        }
      }
      return baseAngles[partName] !== undefined ? baseAngles[partName] : 0
    },
    
    rotateToPartView(partName) {
      console.log('Viewer3D: rotateToPartView called for', partName)  // ← 添加这一行
      console.log('Viewer3D: currentModel =', this.currentModel ? 'exists' : 'null')  // ← 添加这一行
      if (!this.currentModel) return
      
      const currentTarget = this.controls.target.clone()
      const currentPosition = this.camera.position.clone()
      const currentHeight = currentPosition.y
      const dx = currentPosition.x - currentTarget.x
      const dz = currentPosition.z - currentTarget.z
      const horizontalDistance = Math.sqrt(dx * dx + dz * dz)
      let targetAngle = this.getPartAngle(partName)
      let currentAngle = Math.atan2(dx, dz)
      let angleDiff = targetAngle - currentAngle
      while (angleDiff > Math.PI) angleDiff -= Math.PI * 2
      while (angleDiff < -Math.PI) angleDiff += Math.PI * 2
      
      if (Math.abs(angleDiff) < 0.01) return
      
      const startAngle = currentAngle
      const endAngle = currentAngle + angleDiff
      const duration = 300
      const startTime = performance.now()
      
      const animateTransition = (now) => {
        const elapsed = now - startTime
        const t = Math.min(1, elapsed / duration)
        const currentAnimAngle = startAngle + (endAngle - startAngle) * t
        const newX = currentTarget.x + Math.sin(currentAnimAngle) * horizontalDistance
        const newZ = currentTarget.z + Math.cos(currentAnimAngle) * horizontalDistance
        this.camera.position.set(newX, currentHeight, newZ)
        this.controls.target.copy(currentTarget)
        this.camera.lookAt(currentTarget)
        if (t < 1) {
          requestAnimationFrame(animateTransition)
        }
      }
      requestAnimationFrame(animateTransition)
    },
    
    onPointerDown(event) {
        if (!this.currentModel) return;
        if (!this.imagesReady) {
            console.log('Images not ready yet, click detection disabled');
            return;
        }

        const rect = this.renderer.domElement.getBoundingClientRect();
        const clientX = event.clientX !== undefined ? event.clientX : (event.touches && event.touches[0].clientX);
        const clientY = event.clientY !== undefined ? event.clientY : (event.touches && event.touches[0].clientY);
        if (clientX === undefined || clientY === undefined) return;

        this.mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.camera);
        const clickableObjects = [];
        this.currentModel.traverse((child) => {
            if (child.isMesh) clickableObjects.push(child);
        });
        const intersects = this.raycaster.intersectObjects(clickableObjects, true);

        if (intersects.length > 0) {
            const hitObject = intersects[0].object;
            const partKey = this.getPartKeyFromName(hitObject.name);

            if (partKey && this.partsMap.has(partKey)) {
                // 选中部位（不旋转视角）
                this.selectPartOnly(partKey);

                const layers = this.store.partLayersMap.get(partKey);
                if (layers && layers.length > 0) {
                    const uv = intersects[0].uv;
                    if (uv) {
                        const TEXTURE_SIZE = 1024;  // 必须与 compositePartTexture 中的尺寸一致

                        // 从最上层向下检测
                        for (let i = layers.length - 1; i >= 0; i--) {
                            const layer = layers[i];
                            // 确保图片已加载完成
                            if (!layer.img || !layer.img.complete) continue;

                            const img = layer.img;
                            const imgWidth = img.width;
                            const imgHeight = img.height;
                            const imgAspect = imgWidth / imgHeight;
                            const scale = layer.scale || 1;
                            const rotation = layer.rotation || 0;
                            const offsetX = layer.offsetX || 0;
                            const offsetY = layer.offsetY || 0;

                            // 计算绘制区域 (UV 空间)
                            let drawWPx, drawHPx;
                            if (imgAspect >= 1) {
                                drawWPx = scale * (imgWidth / 2);
                                drawHPx = drawWPx / imgAspect;
                            } else {
                                drawHPx = scale * (imgHeight / 2);
                                drawWPx = drawHPx * imgAspect;
                            }

                            const uvW = drawWPx / TEXTURE_SIZE;
                            const uvH = drawHPx / TEXTURE_SIZE;
                            const centerX = 0.5 + offsetX;
                            const centerY = 0.5 - offsetY;
                            const left = centerX - uvW / 2;
                            const right = centerX + uvW / 2;
                            const top = centerY - uvH / 2;
                            const bottom = centerY + uvH / 2;

                            let isInRect = false;
                            const rotationRad = rotation * Math.PI / 180;

                            if (rotationRad !== 0) {
                                const corners = [
                                    { x: left, y: top }, { x: right, y: top },
                                    { x: left, y: bottom }, { x: right, y: bottom }
                                ];
                                const centerXRect = (left + right) / 2;
                                const centerYRect = (top + bottom) / 2;
                                const cos = Math.cos(rotationRad);
                                const sin = Math.sin(rotationRad);
                                const rotated = corners.map(c => ({
                                    x: centerXRect + (c.x - centerXRect) * cos - (c.y - centerYRect) * sin,
                                    y: centerYRect + (c.x - centerXRect) * sin + (c.y - centerYRect) * cos
                                }));
                                const minX = Math.min(...rotated.map(c => c.x));
                                const maxX = Math.max(...rotated.map(c => c.x));
                                const minY = Math.min(...rotated.map(c => c.y));
                                const maxY = Math.max(...rotated.map(c => c.y));
                                isInRect = uv.x >= minX && uv.x <= maxX && uv.y >= minY && uv.y <= maxY;
                            } else {
                                isInRect = uv.x >= left && uv.x <= right && uv.y >= top && uv.y <= bottom;
                            }

                            if (!isInRect) continue;

                            // ★ 针对文字图层进行像素级精确检测
                            if (layer.isText) {
                                const tempCanvas = document.createElement('canvas');
                                tempCanvas.width = imgWidth;
                                tempCanvas.height = imgHeight;
                                const tempCtx = tempCanvas.getContext('2d');
                                tempCtx.drawImage(img, 0, 0);
                                const imageData = tempCtx.getImageData(0, 0, imgWidth, imgHeight);
                                const data = imageData.data;

                                let minXpx = imgWidth, minYpx = imgHeight, maxXpx = 0, maxYpx = 0;
                                let hasPixel = false;
                                for (let y = 0; y < imgHeight; y++) {
                                    for (let x = 0; x < imgWidth; x++) {
                                        const alpha = data[(y * imgWidth + x) * 4 + 3];
                                        if (alpha > 0) {
                                            hasPixel = true;
                                            minXpx = Math.min(minXpx, x);
                                            minYpx = Math.min(minYpx, y);
                                            maxXpx = Math.max(maxXpx, x);
                                            maxYpx = Math.max(maxYpx, y);
                                        }
                                    }
                                }
                                if (!hasPixel) continue;

                                const uvStepX = uvW / imgWidth;
                                const uvStepY = uvH / imgHeight;
                                const textLeftUV = left + minXpx * uvStepX;
                                const textRightUV = left + maxXpx * uvStepX;
                                const textTopUV = top + minYpx * uvStepY;
                                const textBottomUV = top + maxYpx * uvStepY;

                                const padding = 0.02;  // 点击区域宽容度
                                if (!(uv.x >= textLeftUV - padding && uv.x <= textRightUV + padding &&
                                      uv.y >= textTopUV - padding && uv.y <= textBottomUV + padding)) {
                                    continue;
                                }
                            }

                            // 命中图层：清除旧高亮，设置新的 currentEditingLogo
                            if (this.currentEditingLogo) {
                                this.compositePartTexture(this.currentEditingLogo.part, null, false);
                            }
                            this.currentEditingLogo = null;
                            this.hideTextureControls();
                            document.querySelectorAll('.layer-item').forEach(el => el.classList.remove('editing'));

                            if (this.currentHighlightedPart && this.currentHighlightedPart !== partKey) {
                                this.compositePartTexture(this.currentHighlightedPart, null, false);
                            }
                            this.currentHighlightedPart = partKey;
                            this.compositePartTexture(partKey, null, true);

                            // ★★★ 关键修复：更新 store 中的选中状态 ★★★
                            this.selectLogo(partKey, layer.id, layer.isText || false);
                            // 同步本地引用
                            this.currentEditingLogo = { part: partKey, id: layer.id, isText: layer.isText || false };

                            // ★★★ 添加：强制触发 store 更新，确保 TextEditor 能响应 ★★★
                            this.$forceUpdate();
                            this.compositePartTexture(partKey, layer.id);
                            this.showTextureControls();   // ★ 显示纹理控制按钮

                            // 准备拖拽移动
                            this.isDraggingLogo = true;
                            this.isMoving = true;
                            this.lastMouseX = clientX;
                            this.lastMouseY = clientY;
                            this.controls.enabled = false;

                            // 高亮对应的列表项
                            if (layer.isText) {
                                const textEditBtn = document.querySelector(`.edit-text-btn[data-id="${layer.id}"]`);
                                if (textEditBtn) textEditBtn.closest('.layer-item').classList.add('editing');
                            } else {
                                const editBtn = document.querySelector(`.edit-btn[data-id="${layer.id}"]`);
                                if (editBtn) editBtn.closest('.layer-item').classList.add('editing');
                            }
                            return;  // 停止遍历，不再检查更低层的图层
                        }
                    }
                } else {
                    // 该部位没有任何图层，只清除选中状态
                    this.hideTextureControls();
                    if (this.currentEditingLogo) {
                        this.compositePartTexture(this.currentEditingLogo.part, null, false);
                    }
                    this.currentEditingLogo = null;

                    if (this.currentHighlightedPart && this.currentHighlightedPart !== partKey) {
                        this.compositePartTexture(this.currentHighlightedPart, null, false);
                    }
                    this.currentHighlightedPart = partKey;
                    this.compositePartTexture(partKey, null, true);
                }
            }
        } else {
            // 点击空白区域，清除所有选中状态
            if (this.currentEditingLogo) {
                this.compositePartTexture(this.currentEditingLogo.part, null, false);
                this.currentEditingLogo = null;
                this.hideTextureControls();
                document.querySelectorAll('.layer-item').forEach(el => el.classList.remove('editing'));
            }
            if (this.currentHighlightedPart) {
                this.compositePartTexture(this.currentHighlightedPart, null, false);
                this.currentHighlightedPart = null;
            }
        }
    },
    
    onPointerMove(event) {
      if (!this.isDraggingLogo || !this.isMoving || !this.currentEditingLogo) return
      const clientX = event.clientX !== undefined ? event.clientX : (event.touches && event.touches[0].clientX)
      const clientY = event.clientY !== undefined ? event.clientY : (event.touches && event.touches[0].clientY)
      if (clientX === undefined || clientY === undefined) return

      const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
      if (!layers) return
      const targetLayer = layers.find(l => l.id === this.currentEditingLogo.id)
      if (!targetLayer) return

      const deltaX = (clientX - this.lastMouseX) * 0.002
      const deltaY = (clientY - this.lastMouseY) * 0.002
      const newOffsetX = Math.max(-0.5, Math.min(0.5, (targetLayer.offsetX || 0) + deltaX))
      const newOffsetY = Math.max(-0.5, Math.min(0.5, (targetLayer.offsetY || 0) + deltaY))
      
      this.updateLayerProperty(this.currentEditingLogo.part, this.currentEditingLogo.id, 'offsetX', newOffsetX)
      this.updateLayerProperty(this.currentEditingLogo.part, this.currentEditingLogo.id, 'offsetY', newOffsetY)
      
      this.lastMouseX = clientX
      this.lastMouseY = clientY

      this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id)
    },
    
    onPointerUp() {
      if (this.isDraggingLogo) {
        this.isDraggingLogo = false
        this.isMoving = false
        this.controls.enabled = true
        if (this.currentEditingLogo && this.currentEditingLogo.isText) {
          const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
          if (layers) {
            const targetLayer = layers.find(l => l.id === this.currentEditingLogo.id)
            if (targetLayer) {
              this.$emit('sync-text-offset', { id: this.currentEditingLogo.id, offsetX: targetLayer.offsetX, offsetY: targetLayer.offsetY })
            }
          }
        }
        this.$emit('logo-move-end')
      }
    },
    
    onDrop(event) {
      event.preventDefault()
      let dragData = null
      try {
        dragData = JSON.parse(event.dataTransfer.getData('text/plain'))
      } catch (err) {
        return
      }
      if (!dragData || dragData.type !== 'gallery-image') return
      
      const rect = this.renderer.domElement.getBoundingClientRect()
      const mouseX = ((event.clientX - rect.left) / rect.width) * 2 - 1
      const mouseY = -((event.clientY - rect.top) / rect.height) * 2 + 1
      
      this.raycaster.setFromCamera(new THREE.Vector2(mouseX, mouseY), this.camera)
      const clickableObjects = []
      if (this.currentModel) {
        this.currentModel.traverse((child) => {
          if (child.isMesh) clickableObjects.push(child)
        })
      }
      const intersects = this.raycaster.intersectObjects(clickableObjects, true)
      
      let targetPart = null
      if (intersects.length > 0) {
        const hitObject = intersects[0].object
        targetPart = this.getPartKeyFromName(hitObject.name)
      }
      
      if (targetPart && this.partsMap.has(targetPart)) {
        this.$emit('add-image-to-part', { url: dragData.url, part: targetPart, imageId: dragData.imageId })
      } else {
        alert('Please drop the image onto a valid design area (front, back, sleeve, etc.)')
      }
    },
    
    initTextureControls() {
      const scaleDownBtn = this.$refs.scaleDownBtn
      const scaleUpBtn = this.$refs.scaleUpBtn
      const deleteBtn = this.$refs.deleteBtn
      const layerUpBtn = this.$refs.layerUpBtn
      const layerDownBtn = this.$refs.layerDownBtn
      const rotateLeftBtn = this.$refs.rotateLeftBtn
      const rotateRightBtn = this.$refs.rotateRightBtn

      if (!scaleDownBtn || !scaleUpBtn) return

      let scaleInterval = null
      let scaleTimeout = null
      let rotateInterval = null
      let rotateTimeout = null
      const LONG_PRESS_DELAY = 300
      const SCALE_INTERVAL = 50
      const ROTATE_INTERVAL = 50
      const ROTATE_STEP = 15

      // 缩放方法
      const doScaleUp = () => {
        if (!this.currentEditingLogo) return
        const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
        if (!layers) return
        const targetLayer = layers.find(l => l.id === this.currentEditingLogo.id)
        if (targetLayer) {
          const newScale = Math.min(10, (targetLayer.scale || 1) + 0.05)
          this.updateLayerProperty(this.currentEditingLogo.part, this.currentEditingLogo.id, 'scale', newScale)
          this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id)
          if (this.currentEditingLogo.isText) {
            this.$emit('sync-layer-prop', { id: this.currentEditingLogo.id, prop: 'scale', value: newScale })
          }
        }
      }

      const doScaleDown = () => {
        if (!this.currentEditingLogo) return
        const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
        if (!layers) return
        const targetLayer = layers.find(l => l.id === this.currentEditingLogo.id)
        if (targetLayer) {
          const newScale = Math.max(0.3, (targetLayer.scale || 1) - 0.05)
          this.updateLayerProperty(this.currentEditingLogo.part, this.currentEditingLogo.id, 'scale', newScale)
          this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id)
          if (this.currentEditingLogo.isText) {
            this.$emit('sync-layer-prop', { id: this.currentEditingLogo.id, prop: 'scale', value: newScale })
          }
        }
      }

      // 旋转方法
      const doRotateLeft = () => {
        if (!this.currentEditingLogo) return
        const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
        if (!layers) return
        const targetLayer = layers.find(l => l.id === this.currentEditingLogo.id)
        if (targetLayer) {
          const newRotation = ((targetLayer.rotation || 0) - ROTATE_STEP + 360) % 360
          this.updateLayerProperty(this.currentEditingLogo.part, this.currentEditingLogo.id, 'rotation', newRotation)
          this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id)
          if (this.currentEditingLogo.isText) {
            this.$emit('sync-layer-prop', { id: this.currentEditingLogo.id, prop: 'rotation', value: newRotation })
          }
        }
      }

      const doRotateRight = () => {
        if (!this.currentEditingLogo) return
        const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
        if (!layers) return
        const targetLayer = layers.find(l => l.id === this.currentEditingLogo.id)
        if (targetLayer) {
          const newRotation = ((targetLayer.rotation || 0) + ROTATE_STEP) % 360
          this.updateLayerProperty(this.currentEditingLogo.part, this.currentEditingLogo.id, 'rotation', newRotation)
          this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id)
          if (this.currentEditingLogo.isText) {
            this.$emit('sync-layer-prop', { id: this.currentEditingLogo.id, prop: 'rotation', value: newRotation })
          }
        }
      }

      // 删除方法
      const doDelete = () => {
        if (!this.currentEditingLogo) return
        const part = this.currentEditingLogo.part
        const id = this.currentEditingLogo.id
        const isText = this.currentEditingLogo.isText === true
        
        this.deleteLayer(part, id)
        this.hideTextureControls()
        this.$emit('delete-layer', { id, part, isText })
        
        // 立即刷新当前部位的纹理，让删除效果立即显示
        if (this.partsMap && this.partsMap.has(part)) {
          // 强制重新合成纹理
          this.compositePartTexture(part)
        }
        
        // 清除当前选中的图层
        this.currentEditingLogo = null
        this.clearLogoSelection()
      }

      // 图层上移
      const doLayerUp = () => {
        if (!this.currentEditingLogo) return
        const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
        if (!layers || layers.length < 2) return
        const idx = layers.findIndex(l => l.id === this.currentEditingLogo.id)
        if (idx === -1 || idx >= layers.length - 1) return
        this.reorderLayers(this.currentEditingLogo.part, idx, idx + 1)
        this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id)
      }

      // 图层下移
      const doLayerDown = () => {
        if (!this.currentEditingLogo) return
        const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
        if (!layers || layers.length < 2) return
        const idx = layers.findIndex(l => l.id === this.currentEditingLogo.id)
        if (idx === -1 || idx <= 0) return
        this.reorderLayers(this.currentEditingLogo.part, idx, idx - 1)
        this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id)
      }

      // 停止缩放
      const stopScaling = () => {
        if (scaleTimeout) { clearTimeout(scaleTimeout); scaleTimeout = null }
        if (scaleInterval) { clearInterval(scaleInterval); scaleInterval = null }
      }

      // 停止旋转
      const stopRotating = () => {
        if (rotateTimeout) { clearTimeout(rotateTimeout); rotateTimeout = null }
        if (rotateInterval) { clearInterval(rotateInterval); rotateInterval = null }
      }

      // 绑定缩小按钮事件（支持长按）
      scaleDownBtn.addEventListener('click', (e) => { e.stopPropagation(); doScaleDown() })
      scaleDownBtn.addEventListener('pointerdown', (e) => {
        e.stopPropagation(); e.preventDefault()
        stopScaling()
        scaleTimeout = setTimeout(() => {
          scaleInterval = setInterval(doScaleDown, SCALE_INTERVAL)
        }, LONG_PRESS_DELAY)
      })
      scaleDownBtn.addEventListener('pointerup', stopScaling)
      scaleDownBtn.addEventListener('pointerleave', stopScaling)
      scaleDownBtn.addEventListener('pointercancel', stopScaling)

      // 绑定放大按钮事件（支持长按）
      scaleUpBtn.addEventListener('click', (e) => { e.stopPropagation(); doScaleUp() })
      scaleUpBtn.addEventListener('pointerdown', (e) => {
        e.stopPropagation(); e.preventDefault()
        stopScaling()
        scaleTimeout = setTimeout(() => {
          scaleInterval = setInterval(doScaleUp, SCALE_INTERVAL)
        }, LONG_PRESS_DELAY)
      })
      scaleUpBtn.addEventListener('pointerup', stopScaling)
      scaleUpBtn.addEventListener('pointerleave', stopScaling)
      scaleUpBtn.addEventListener('pointercancel', stopScaling)

      // 绑定左旋转按钮事件（支持长按）
      if (rotateLeftBtn) {
        rotateLeftBtn.addEventListener('click', (e) => { e.stopPropagation(); doRotateLeft() })
        rotateLeftBtn.addEventListener('pointerdown', (e) => {
          e.stopPropagation(); e.preventDefault()
          stopRotating()
          rotateTimeout = setTimeout(() => {
            rotateInterval = setInterval(doRotateLeft, ROTATE_INTERVAL)
          }, LONG_PRESS_DELAY)
        })
        rotateLeftBtn.addEventListener('pointerup', stopRotating)
        rotateLeftBtn.addEventListener('pointerleave', stopRotating)
        rotateLeftBtn.addEventListener('pointercancel', stopRotating)
      }

      // 绑定右旋转按钮事件（支持长按）
      if (rotateRightBtn) {
        rotateRightBtn.addEventListener('click', (e) => { e.stopPropagation(); doRotateRight() })
        rotateRightBtn.addEventListener('pointerdown', (e) => {
          e.stopPropagation(); e.preventDefault()
          stopRotating()
          rotateTimeout = setTimeout(() => {
            rotateInterval = setInterval(doRotateRight, ROTATE_INTERVAL)
          }, LONG_PRESS_DELAY)
        })
        rotateRightBtn.addEventListener('pointerup', stopRotating)
        rotateRightBtn.addEventListener('pointerleave', stopRotating)
        rotateRightBtn.addEventListener('pointercancel', stopRotating)
      }

      // 绑定删除按钮
      if (deleteBtn) {
        deleteBtn.addEventListener('click', (e) => { e.stopPropagation(); doDelete() })
      }

      // 绑定图层上移按钮
      if (layerUpBtn) {
        layerUpBtn.addEventListener('click', (e) => { e.stopPropagation(); doLayerUp() })
      }

      // 绑定图层下移按钮
      if (layerDownBtn) {
        layerDownBtn.addEventListener('click', (e) => { e.stopPropagation(); doLayerDown() })
      }
    },
    
    doScaleUp() {
      if (!this.currentEditingLogo) return
      const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
      if (!layers) return
      const targetLayer = layers.find(l => l.id === this.currentEditingLogo.id)
      if (targetLayer) {
        const newScale = Math.min(10, (targetLayer.scale || 1) + 0.05)
        this.updateLayerProperty(this.currentEditingLogo.part, this.currentEditingLogo.id, 'scale', newScale)
        this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id)
        if (this.currentEditingLogo.isText) {
          this.$emit('sync-layer-prop', { id: this.currentEditingLogo.id, prop: 'scale', value: newScale })
        }
      }
    },
    
    doScaleDown() {
      if (!this.currentEditingLogo) return
      const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
      if (!layers) return
      const targetLayer = layers.find(l => l.id === this.currentEditingLogo.id)
      if (targetLayer) {
        const newScale = Math.max(0.3, (targetLayer.scale || 1) - 0.05)
        this.updateLayerProperty(this.currentEditingLogo.part, this.currentEditingLogo.id, 'scale', newScale)
        this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id)
        if (this.currentEditingLogo.isText) {
          this.$emit('sync-layer-prop', { id: this.currentEditingLogo.id, prop: 'scale', value: newScale })
        }
      }
    },
    
    doRotate(degrees) {
      if (!this.currentEditingLogo) return
      const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
      if (!layers) return
      const targetLayer = layers.find(l => l.id === this.currentEditingLogo.id)
      if (targetLayer) {
        const newRotation = ((targetLayer.rotation || 0) + degrees + 360) % 360
        this.updateLayerProperty(this.currentEditingLogo.part, this.currentEditingLogo.id, 'rotation', newRotation)
        this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id)
        if (this.currentEditingLogo.isText) {
          this.$emit('sync-layer-prop', { id: this.currentEditingLogo.id, prop: 'rotation', value: newRotation })
        }
      }
    },
    
    doDelete() {
      if (!this.currentEditingLogo) return
      const part = this.currentEditingLogo.part
      const id = this.currentEditingLogo.id
      const isText = this.currentEditingLogo.isText === true
      
      this.deleteLayer(part, id)
      this.hideTextureControls()
      this.$emit('delete-layer', { id, part, isText })
    },
    
    doLayerUp() {
      if (!this.currentEditingLogo) return
      const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
      if (!layers || layers.length < 2) return
      const idx = layers.findIndex(l => l.id === this.currentEditingLogo.id)
      if (idx === -1 || idx >= layers.length - 1) return
      this.reorderLayers(this.currentEditingLogo.part, idx, idx + 1)
      this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id)
    },
    
    doLayerDown() {
      if (!this.currentEditingLogo) return
      const layers = this.store.partLayersMap.get(this.currentEditingLogo.part)
      if (!layers || layers.length < 2) return
      const idx = layers.findIndex(l => l.id === this.currentEditingLogo.id)
      if (idx === -1 || idx <= 0) return
      this.reorderLayers(this.currentEditingLogo.part, idx, idx - 1)
      this.compositePartTexture(this.currentEditingLogo.part, this.currentEditingLogo.id)
    },
    
    showTextureControls() {
        const tryShow = () => {
            const control = this.$refs.textureControl;
            if (control) {
                control.style.display = 'flex';
                this.updateTextureControlPosition();
            } else {
                setTimeout(tryShow, 50);
            }
        };
        tryShow();
    },
    
    hideTextureControls() {
      const control = this.$refs.textureControl
      if (control) control.style.display = 'none'
    },
    
    getModelBottomScreenY() {
      if (!this.currentModel) return null
      const box = new THREE.Box3().setFromObject(this.currentModel)
      const minY = box.min.y
      const bottomPoint = new THREE.Vector3(0, minY, 0)
      const screenPoint = bottomPoint.project(this.camera)
      const canvasRect = this.renderer.domElement.getBoundingClientRect()
      const viewerArea = document.querySelector('.viewer-area')
      const viewerRect = viewerArea ? viewerArea.getBoundingClientRect() : canvasRect
      const screenY = ((1 - screenPoint.y) / 2) * canvasRect.height
      return screenY + (canvasRect.top - viewerRect.top)
    },
    
    updateTextureControlPosition() {
      const control = this.$refs.textureControl
      const canvas = this.renderer?.domElement
      if (!control || control.style.display !== 'flex' || !this.currentModel || !canvas) return
      
      requestAnimationFrame(() => {
        const modelBottomY = this.getModelBottomScreenY()
        if (modelBottomY === null) return
        const offsetPx = 20
        const viewerArea = document.querySelector('.viewer-area')
        const viewerRect = viewerArea ? viewerArea.getBoundingClientRect() : null
        let topPos = modelBottomY + offsetPx
        if (viewerRect) {
          const controlHeight = control.offsetHeight
          const maxTop = viewerRect.height - controlHeight - 10
          topPos = Math.min(topPos, maxTop)
          topPos = Math.max(topPos, 10)
        }
        control.style.top = `${topPos}px`
        control.style.left = '50%'
        control.style.transform = 'translateX(-50%)'
      })
    },
    
    initAutoRotate() {
      this.resetIdleTimer()
      const interact = () => { this.resetIdleTimer() }
      const canvasEvents = ['mousedown', 'mousemove', 'wheel', 'touchstart', 'touchmove']
      canvasEvents.forEach(e => this.renderer.domElement.addEventListener(e, interact))
    },
    
    resetIdleTimer() {
      this.stopAutoRotate()
      if (this.autoRotateTimer) clearTimeout(this.autoRotateTimer)
      this.autoRotateTimer = setTimeout(() => {
        this.startAutoRotate()
      }, 7000)
    },
    
    startAutoRotate() {
      this.autoRotateEnabled = true
    },
    
    stopAutoRotate() {
      this.autoRotateEnabled = false
    },
    
    onWindowResize() {
      const container = this.$refs.canvasContainer
      const w = container.clientWidth
      const h = container.clientHeight
      this.camera.aspect = w / h
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(w, h)
    }
  }
}
</script>

<style scoped>
.viewer-area {
  flex: 2.7;
  min-width: 280px;
  position: relative;
  overflow: hidden;
}

.viewer {
  background: #ffffff;
  border-radius: 0;
  overflow: hidden;
  aspect-ratio: 1 / 1;
  width: 100%;
  margin: 0;
}

/* 按钮（缩小、放大、向下移一层、向上移一层、删除）*/
.texture-control {
  position: absolute;
  z-index: 100;
  display: none;
  pointer-events: auto;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  padding: 6px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  max-width: calc(100vw - 20px);
  box-sizing: border-box;
}

.scale-down-handle,
.scale-up-handle,
.delete-handle,
.layer-up-handle,
.layer-down-handle,
.rotate-left-handle,
.rotate-right-handle {
  width: 36px;
  min-width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
  position: relative;
  padding: 0;
}

.scale-down-handle,
.scale-up-handle {
  background: #0026e6;
}

.delete-handle {
  background: #ef4444;
}

.layer-up-handle,
.layer-down-handle {
  background: #10b981;
}

.rotate-left-handle,
.rotate-right-handle {
  background: #8b5cf6;
}

.scale-down-handle:hover,
.scale-up-handle:hover,
.delete-handle:hover,
.layer-up-handle:hover,
.layer-down-handle:hover,
.rotate-left-handle:hover,
.rotate-right-handle:hover {
  transform: scale(1.1);
  filter: brightness(1.2);
}

@media (max-width: 850px) {
  .viewer-area {
    margin-bottom: 0.5rem;
    margin-top: -20px;
    padding: 0;
  }
  .viewer {
    height: 40vh;
    margin-top: -15px;
    margin-bottom: 0;
  }
  .texture-control {
    gap: 6px;
    padding: 4px 6px;
    max-width: calc(100vw - 10px);
    flex-wrap: nowrap;
  }
  /* ★★★ 统一所有按钮的尺寸 ★★★ */
  .scale-down-handle,
  .scale-up-handle,
  .delete-handle,
  .layer-up-handle,
  .layer-down-handle,
  .rotate-left-handle,
  .rotate-right-handle {
    min-width: 36px !important;
    width: 36px !important;
    height: 36px !important;
    padding: 0 !important;
    font-size: 12px;
    border-radius: 8px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
</style>